import { IN_TRAY } from '../model/areas.js';
import type { BoardData, CatalogEntry, Item } from '../model/schema.js';
import { isListedChild } from '../model/hierarchy.js';
import { makeItem } from '../model/board.js';
import type { ChannelObjectPlan } from './fromChannels.js';
import {
  applyChannelObjectPlan,
  plannableChannels,
  planObjectsFromChannels,
} from './fromChannels.js';
import { normalizeStandText, standOf } from './stands.js';

/**
 * Placing the objects of the input list on the stage (OIL-36/37): which objects belong to a
 * channel, how they are grouped into one spot each (a stand with what is mounted on it) and what
 * the library section "From the input list" offers.
 */

/** Objects a channel brings onto the stage: its source item (mic/DI …) and that item's stand. */
export function channelObjectIds(data: BoardData, channelId: string): string[] {
  const ch = data.channels[channelId];
  const source = ch?.sourceItemId === undefined ? undefined : data.items[ch.sourceItemId];
  if (source === undefined) return [];
  const stand = standOf(data, source);
  return stand === undefined ? [source.id] : [source.id, stand.id];
}

/**
 * Groups items into spots: a stand and the items mounted on it share one spot (the mic stays on
 * its stand), everything else gets its own. Order follows the first occurrence in `ids`; unknown
 * ids and duplicates are dropped. `withStands` adds the stands mounted items stand on.
 */
export function mountSlots(
  data: BoardData,
  ids: readonly string[],
  opts: { withStands?: boolean } = {},
): string[][] {
  const wanted = new Set(ids.filter((id) => data.items[id] !== undefined));
  if (opts.withStands === true)
    for (const id of [...wanted]) {
      const stand = standOf(data, data.items[id]);
      if (stand !== undefined) wanted.add(stand.id);
    }
  const slots = new Map<string, string[]>();
  const keyOf = (item: Item): string => {
    const stand = standOf(data, item);
    return stand !== undefined && wanted.has(stand.id) ? stand.id : item.id;
  };
  for (const id of ids) {
    if (!wanted.has(id)) continue;
    const key = keyOf(data.items[id]!);
    const slot = slots.get(key) ?? [];
    if (!slots.has(key)) slots.set(key, slot);
    if (!slot.includes(id)) slot.push(id);
  }
  // Stands only added via `withStands` join the spot of their first mounted item.
  for (const id of wanted) if (!ids.includes(id)) slots.get(id)!.push(id);
  return [...slots.values()];
}

export interface ChannelPlacementEntry {
  channelId: string;
  number: number;
  name: string;
  /** Objects not on the stage yet (source first, then its stand); empty while `pending`. */
  itemIds: string[];
  /** No source item yet: placing creates it (and the stand) from the channel's texts. */
  pending: boolean;
  /** Informal source / stand texts of a pending channel ('' when none). */
  sourceText: string;
  standText: string;
}

/**
 * Library section "From the input list" (OIL-36): per channel, by number, the channel's objects
 * that are not on the stage (unplaced mic / stand / DI), or – for channels without objects yet
 * but with a source or stand text – a pending entry. Children shown in a parent's list box are
 * skipped (placing them would not draw them).
 */
export function channelPlacementEntries(data: BoardData): ChannelPlacementEntry[] {
  const pending = new Set(plannableChannels(data).map((c) => c.id));
  const out: ChannelPlacementEntry[] = [];
  const channels = Object.values(data.channels).sort(
    (a, b) => a.number - b.number || a.id.localeCompare(b.id),
  );
  for (const ch of channels) {
    const base = { channelId: ch.id, number: ch.number, name: ch.name };
    if (pending.has(ch.id)) {
      out.push({
        ...base,
        itemIds: [],
        pending: true,
        sourceText: (ch.sourceText ?? '').trim(),
        standText: normalizeStandText(ch.stand ?? ''),
      });
      continue;
    }
    const itemIds = channelObjectIds(data, ch.id).filter((id) => {
      const item = data.items[id]!;
      return item.unplaced === true && !isListedChild(data, item);
    });
    if (itemIds.length > 0)
      out.push({ ...base, itemIds, pending: false, sourceText: '', standText: '' });
  }
  return out;
}

export interface ChannelPlacement {
  /** Objects to create first (channels without source items). */
  plan: ChannelObjectPlan;
  /** The board after the plan. */
  data: BoardData;
  /** One spot per channel (in the given order): its source item and stand. */
  slots: string[][];
}

/**
 * Objects of the given channels, creating missing ones from the channel texts first (the same
 * plan as "Create objects from input list"). Channels that bring nothing are left out.
 */
export function planChannelPlacement(
  data: BoardData,
  catalog: readonly CatalogEntry[],
  channelIds: readonly string[],
  opts: { newId?: () => string } = {},
): ChannelPlacement {
  const plan = planObjectsFromChannels(data, catalog, { ...opts, channelIds });
  const next = applyChannelObjectPlan(data, plan);
  const slots = [...new Set(channelIds)]
    .map((id) => channelObjectIds(next, id))
    .filter((s) => s.length > 0);
  return { plan, data: next, slots };
}

export interface ChannelListPlacement extends ChannelPlacement {
  /** The new list object (shows the channels' sources as rows, decision U2). */
  list: Item;
}

/** Name of a channel list: the channels' common group, else the channel range. */
export function channelListLabel(data: BoardData, channelIds: readonly string[]): string {
  const chans = channelIds.map((id) => data.channels[id]).filter((c) => c !== undefined);
  const numbers = chans.map((c) => c.number).sort((a, b) => a - b);
  const range =
    numbers.length === 0
      ? ''
      : numbers[0] === numbers.at(-1)
        ? `Ch ${numbers[0]}`
        : `Ch ${numbers[0]}–${numbers.at(-1)}`;
  const groups = [...new Set(chans.map((c) => c.group?.trim() ?? ''))];
  const group = groups.length === 1 ? groups[0]! : '';
  return group !== '' ? `${group} (${range})` : range;
}

/**
 * The selected channels as ONE list object on the stage (OIL-55, e.g. the drum kit): missing
 * sources and stands are created from the channel texts first, then a list object takes the
 * sources as children (their stands are shown in the rows, not on the canvas).
 */
export function planChannelList(
  data: BoardData,
  catalog: readonly CatalogEntry[],
  channelIds: readonly string[],
  opts: { newId?: () => string } = {},
): ChannelListPlacement {
  const placement = planChannelPlacement(data, catalog, channelIds, opts);
  const next = placement.data;
  const list = makeItem('generic', {
    ...(opts.newId ? { id: opts.newId() } : {}),
    label: channelListLabel(next, channelIds),
    childDisplay: 'list',
    ...IN_TRAY,
  });
  const sources = [...new Set(channelIds.map((id) => next.channels[id]?.sourceItemId))].filter(
    (id): id is string => id !== undefined && next.items[id] !== undefined,
  );
  const children = sources.map((id) => {
    const child: Item = { ...next.items[id]!, parentId: list.id };
    delete child.detached;
    return child;
  });
  const created = new Map(placement.plan.items.map((i) => [i.id, i]));
  for (const c of children) created.set(c.id, c);
  const items = [list, ...created.values()];
  return {
    plan: { ...placement.plan, items },
    data: {
      ...next,
      items: { ...next.items, ...Object.fromEntries(items.map((i) => [i.id, i])) },
    },
    slots: [[list.id]],
    list,
  };
}
